import { z } from 'zod'
import { instantSchema } from '../validators/temporal.validator.js'
import { zoneSchema } from '../dto/zone.dto.js'
import { staticLabelSchema } from '../dto/static-label.dto.js'
import { furnitureAreasSchema } from '../dto/furniture-area.dto.js'
import { landmarksSchema } from '../dto/landmark.dto.js'
import { timezoneSchema } from '../validators/timezone.validator.js'
import { calendarDaySchema } from '../validators/calendar-day.validator.js'
import {
  DEFAULT_VENUE_WINDOW,
  endsLaterInDay,
  timeOfDaySchema,
  type TimeOfDay,
} from '../validators/time-of-day.validator.js'

/**
 * A venue's usual evening must stay inside one day.
 *
 * Individual events may run past midnight and carry the next day's date on their
 * end; a venue's *default* may not, because late nights are not the standard
 * pattern here and a default that wrapped would need a rule of its own for no
 * one's benefit. See `docs/event-scheduling.md`.
 */
const defaultWindowEndsLater = {
  message: 'The default end time must be later in the day than the start',
  path: ['defaultEndsAt'],
}

function windowInOneDay(v: {
  defaultStartsAt?: TimeOfDay | undefined
  defaultEndsAt?: TimeOfDay | undefined
}): boolean {
  // Only when both are present: a partial *update* that touches one of them is
  // checked against the stored other in the service, which is the only place
  // that knows it. At create there is no stored other — the create schema
  // defaults both halves so this always has a pair to judge.
  if (v.defaultStartsAt === undefined || v.defaultEndsAt === undefined) return true
  return endsLaterInDay(v.defaultStartsAt, v.defaultEndsAt)
}

/** Assign a venue-admin user to a venue they manage, by user id. */
export const assignVenueManagerInputSchema = z.object({ userId: z.string().uuid() }).strict()

export type AssignVenueManagerInput = z.infer<typeof assignVenueManagerInputSchema>

/**
 * Update a venue's own fields — its name and/or address. `address` may be set to
 * `null` to clear it. `.strict()` rejects unknown keys.
 */
export const updateVenueInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    address: z.string().trim().max(300).nullable().optional(),
    supportsConcurrentEvents: z.boolean().optional(),
    /**
     * The venue's clock. Nullable because it is entered per venue rather than
     * guessed — a wrong zone shifts every "has this finished?" silently, which
     * is worse than an empty field that asks to be filled.
     */
    timezone: timezoneSchema.nullable().optional(),
    /** The venue's usual evening, in its own clock. */
    defaultStartsAt: timeOfDaySchema.optional(),
    defaultEndsAt: timeOfDaySchema.optional(),
    /** Set by the Prism import so a later run finds this venue by id, not by name. */
    prismmVenueRef: z.number().int().positive().nullable().optional(),
  })
  .strict()
  .refine(windowInOneDay, defaultWindowEndsLater)

export type UpdateVenueInput = z.infer<typeof updateVenueInputSchema>

/**
 * Create a venue under the caller's tenant. Only its own fields — spaces,
 * tracks, and inventory are added afterward. `.strict()` rejects unknown keys.
 */
export const createVenueInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    address: z.string().trim().max(300).nullable().optional(),
    /** Optional at creation; the venue is unusable for scheduling until it is set. */
    timezone: timezoneSchema.nullable().optional(),
    /**
     * Defaulted rather than optional, so the one-day rule below always sees a
     * pair. Left optional, `{ defaultStartsAt: '23:30' }` was accepted and
     * paired with the column's own 23:00 end to store an inverted window — the
     * very shape the update path refuses.
     */
    defaultStartsAt: timeOfDaySchema.default(DEFAULT_VENUE_WINDOW.startsAt),
    defaultEndsAt: timeOfDaySchema.default(DEFAULT_VENUE_WINDOW.endsAt),
    prismmVenueRef: z.number().int().positive().nullable().optional(),
  })
  .strict()
  .refine(windowInOneDay, defaultWindowEndsLater)

export type CreateVenueInput = z.infer<typeof createVenueInputSchema>

/**
 * A venue's clock and its usual evening.
 *
 * Its own input, and its own route, for the reason the cover photograph has one:
 * these are facts about how the venue runs, which is a VENUE_ADMIN's business,
 * while the name and the address are tenant-level facts belonging to an
 * ORGANIZATION_ADMIN. Folded into `updateVenueInputSchema` the clock could only
 * be set by an organization admin at organization level — so the venue's own
 * staff, who are the people who know what time it closes, could see the setting
 * and not save it.
 *
 * Every field optional: the form saves the three together, but a caller that
 * only wants the zone should not have to restate the evening.
 */
export const updateVenueScheduleInputSchema = z
  .object({
    timezone: timezoneSchema.nullable().optional(),
    defaultStartsAt: timeOfDaySchema.optional(),
    defaultEndsAt: timeOfDaySchema.optional(),
  })
  .strict()
  .refine(windowInOneDay, defaultWindowEndsLater)

export type UpdateVenueScheduleInput = z.infer<typeof updateVenueScheduleInputSchema>

/** Create an event track (a bookable partition) in a venue. */
export const createEventTrackInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
  })
  .strict()

export type CreateEventTrackInput = z.infer<typeof createEventTrackInputSchema>

/**
 * Update an event track — rename, reorder, and/or set which spaces belong to it.
 * Setting `spaceIds` moves those spaces onto this track (and off any other), and
 * their pinned stock follows. `.strict()` rejects unknown keys.
 */
export const updateEventTrackInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    sortOrder: z.number().int().optional(),
    spaceIds: z.array(z.string().uuid()).optional(),
  })
  .strict()

export type UpdateEventTrackInput = z.infer<typeof updateEventTrackInputSchema>

/**
 * Update a venue space's background (Build mode) — any subset of name,
 * dimensions, zones, and labels. `.strict()` rejects unknown keys.
 */
export const updateVenueSpaceInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    width: z.number().positive().optional(),
    height: z.number().positive().optional(),
    zones: z.array(zoneSchema).optional(),
    labels: z.array(staticLabelSchema).optional(),
    areas: furnitureAreasSchema.optional(),
    landmarks: landmarksSchema.optional(),
    /** Set by the Prism import so a later run finds this room by id, not by name. */
    prismmHallId: z.number().int().positive().nullable().optional(),
    /** Take the room out of use (true) or put it back (false). Its plans are untouched. */
    retired: z.boolean().optional(),
  })
  .strict()

export type UpdateVenueSpaceInput = z.infer<typeof updateVenueSpaceInputSchema>

/**
 * Create a space in a venue. Its name and starting dimensions (in the editor's
 * pixel units); zones/labels start empty and are drawn in the background editor.
 * `.strict()` rejects unknown keys.
 */
export const createVenueSpaceInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    width: z.number().positive(),
    height: z.number().positive(),
    prismmHallId: z.number().int().positive().nullable().optional(),
  })
  .strict()

export type CreateVenueSpaceInput = z.infer<typeof createVenueSpaceInputSchema>

/**
 * Author a new version (a "remodel") of a space. It starts as a copy of the
 * space's currently-effective settings and inventory, taking effect on
 * `effectiveFrom`; events on or after that date inherit it.
 */
export const createSpaceVersionInputSchema = z
  .object({
    label: z.string().trim().min(1).max(120),
    effectiveFrom: calendarDaySchema,
  })
  .strict()

export type CreateSpaceVersionInput = z.infer<typeof createSpaceVersionInputSchema>

/**
 * Update one version of a space — its label, effective date, and/or background
 * settings. The original version is "always" effective and its date cannot be
 * set. `.strict()` rejects unknown keys.
 */
export const updateSpaceVersionInputSchema = z
  .object({
    label: z.string().trim().min(1).max(120).optional(),
    effectiveFrom: calendarDaySchema.optional(),
    width: z.number().positive().optional(),
    height: z.number().positive().optional(),
    zones: z.array(zoneSchema).optional(),
    labels: z.array(staticLabelSchema).optional(),
    areas: furnitureAreasSchema.optional(),
    landmarks: landmarksSchema.optional(),
    /**
     * The version's `versionUpdatedAt` as the editor last saw it. When given and
     * the version has been written since, the edit is refused (409) rather than
     * overwriting somebody else's drawing.
     */
    expectedUpdatedAt: instantSchema.optional(),
  })
  .strict()

export type UpdateSpaceVersionInput = z.infer<typeof updateSpaceVersionInputSchema>
